/**
 * AdmissionGuidance - Proactive Admission Workflow Guidance Component
 * 
 * Provides:
 * - Step-by-step guidance for new admissions
 * - Discipline-specific prompts (RN, SW, Chaplain, Aide)
 * - Visual timeline with completion tracking
 * - One-click launch to correct visit template
 * - Medicare compliance indicators
 */

import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Progress } from "./ui/progress";
import { ScrollArea } from "./ui/scroll-area";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "./ui/tooltip";
import {
  CheckCircle2,
  Circle,
  Clock,
  AlertTriangle,
  AlertCircle,
  ChevronRight,
  Play,
  FileText,
  Users,
  Heart,
  BookHeart,
  Stethoscope,
  Home,
  ClipboardList,
  UserCheck,
  Lightbulb,
  ArrowRight,
  Mic,
  Info,
  Sparkles,
  Target,
  Calendar
} from "lucide-react";
import { toast } from "sonner";

// Medicare admission timeline requirements
const ADMISSION_TIMELINE = [
  {
    id: "admission_visit",
    title: "RN Admission Visit",
    discipline: "rn",
    hours: 48,
    description: "Complete comprehensive admission assessment including comfort kit education, medication review, and initial care plan",
    requirements: [
      "Comprehensive physical assessment",
      "Pain and symptom evaluation", 
      "Medication reconciliation",
      "Comfort kit education",
      "Emergency plan review",
      "Initial care plan started"
    ],
    icon: Home,
    color: "blue",
    bodySystemsFocus: ["cardiovascular", "respiratory", "pain", "functional_status"]
  },
  {
    id: "comprehensive_visit",
    title: "Comprehensive/SFV Visit",
    discipline: "rn",
    hours: 96,
    description: "Symptom Focused Visit required if patient has moderate-severe symptoms at admission",
    requirements: [
      "Full physical reassessment",
      "Symptom management evaluation",
      "Medication effectiveness review",
      "Care plan adjustments"
    ],
    icon: Stethoscope,
    color: "purple",
    conditional: true,
    conditionLabel: "Required for moderate-severe symptoms",
    bodySystemsFocus: ["pain", "respiratory", "neurological", "gastrointestinal"]
  },
  {
    id: "sw_assessment",
    title: "Social Worker Assessment",
    discipline: "sw",
    hours: 120,
    description: "Complete psychosocial assessment, advance directive review, and resource coordination",
    requirements: [
      "Psychosocial assessment",
      "Advance directive review/completion",
      "Caregiver support assessment",
      "Resource and benefit coordination",
      "Goals of care discussion"
    ],
    icon: Heart,
    color: "pink",
    bodySystemsFocus: ["mental_status", "functional_status"]
  },
  {
    id: "chaplain_assessment",
    title: "Chaplain/Spiritual Care",
    discipline: "chaplain",
    hours: 120,
    description: "Complete spiritual assessment and identify patient/family spiritual needs",
    requirements: [
      "Spiritual beliefs assessment",
      "Religious/cultural preferences",
      "Meaning and purpose exploration",
      "Spiritual distress screening",
      "Support resources offered"
    ],
    icon: BookHeart,
    color: "purple",
    bodySystemsFocus: ["mental_status"]
  },
  {
    id: "bereavement_contact",
    title: "Bereavement Coordinator Contact",
    discipline: "bereavement",
    hours: 120,
    description: "Initial contact with family to introduce bereavement services and assess anticipatory grief",
    requirements: [
      "Introduction to bereavement services",
      "Anticipatory grief assessment",
      "Family coping evaluation",
      "13-month support plan discussed"
    ],
    icon: Users,
    color: "teal",
    bodySystemsFocus: []
  },
  {
    id: "initial_poc",
    title: "Initial Plan of Care",
    discipline: "rn",
    hours: 120,
    description: "Complete interdisciplinary care plan with all IDG signatures",
    requirements: [
      "All IDG assessments complete",
      "Patient/family goals documented",
      "Care interventions outlined",
      "All discipline signatures obtained",
      "Patient/caregiver signatures"
    ],
    icon: ClipboardList,
    color: "amber",
    dependsOn: ["admission_visit", "sw_assessment", "chaplain_assessment"],
    bodySystemsFocus: []
  },
  {
    id: "idg_review",
    title: "First IDG Review",
    discipline: "all",
    hours: 120,
    description: "Present patient at IDG meeting for initial care plan review and team approval",
    requirements: [
      "All assessments presented",
      "Care plan reviewed by team",
      "Goals and interventions approved",
      "Visit frequency established"
    ],
    icon: UserCheck,
    color: "green",
    dependsOn: ["initial_poc"],
    bodySystemsFocus: []
  }
];

// Discipline configuration
const DISCIPLINE_CONFIG = {
  rn: { name: "Registered Nurse", shortName: "RN", color: "bg-blue-500" },
  sw: { name: "Social Worker", shortName: "SW", color: "bg-pink-500" },
  chaplain: { name: "Chaplain", shortName: "Chap", color: "bg-purple-500" },
  bereavement: { name: "Bereavement Coordinator", shortName: "BRV", color: "bg-teal-500" },
  aide: { name: "Home Health Aide", shortName: "HHA", color: "bg-orange-500" },
  all: { name: "All Disciplines", shortName: "IDG", color: "bg-green-500" }
};

export default function AdmissionGuidance({ 
  workflow, 
  tasks = [], 
  userRole = "rn",
  onLaunchTemplate,
  onComplete,
  compact = false
}) {
  const [expandedStep, setExpandedStep] = useState(null);
  const [showGuidanceDialog, setShowGuidanceDialog] = useState(false);
  const [currentGuidance, setCurrentGuidance] = useState(null);

  // Calculate completion status for each step
  const getStepStatus = (stepId) => {
    const task = tasks.find(t => t.task_type === stepId);
    if (!task) return "pending";
    if (task.status === "completed") return "completed";
    if (task.compliance_status === "overdue") return "overdue";
    if (task.compliance_status === "critical") return "critical";
    if (task.compliance_status === "warning") return "at_risk";
    return "pending";
  };

  // Get next action for user's role
  const getNextAction = () => {
    for (const step of ADMISSION_TIMELINE) {
      // Skip conditional steps if not required
      if (step.conditional && workflow && !workflow.requires_sfv && step.id === "comprehensive_visit") {
        continue;
      }
      
      const status = getStepStatus(step.id);
      if (status !== "completed") {
        // Check if this step is for the current user's role or "all"
        if (step.discipline === userRole || step.discipline === "all" || 
            (userRole === "rn" && ["rn", "bereavement"].includes(step.discipline))) {
          return step;
        }
      }
    }
    return null;
  };

  // Get steps relevant to current user
  const getMySteps = () => {
    return ADMISSION_TIMELINE.filter(step => {
      if (step.conditional && workflow && !workflow.requires_sfv && step.id === "comprehensive_visit") {
        return false;
      }
      return step.discipline === userRole || step.discipline === "all" ||
             (userRole === "rn" && step.discipline === "bereavement");
    });
  };

  // Calculate overall progress
  const calculateProgress = () => {
    const relevantSteps = ADMISSION_TIMELINE.filter(step => {
      if (step.conditional && workflow && !workflow.requires_sfv && step.id === "comprehensive_visit") {
        return false;
      }
      return true;
    });
    
    const completed = relevantSteps.filter(step => getStepStatus(step.id) === "completed").length;
    return Math.round((completed / relevantSteps.length) * 100);
  };

  // Format time remaining
  const formatTimeRemaining = (deadline) => {
    if (!deadline) return null;
    const now = new Date();
    const deadlineDate = new Date(deadline);
    const hours = Math.round((deadlineDate - now) / (1000 * 60 * 60));
    
    if (hours < 0) return { text: `${Math.abs(hours)}h overdue`, urgent: true };
    if (hours < 4) return { text: `${hours}h left`, urgent: true };
    if (hours < 24) return { text: `${hours}h left`, urgent: false };
    return { text: `${Math.round(hours / 24)}d left`, urgent: false };
  };

  // Get task for step
  const getTaskForStep = (stepId) => {
    return tasks.find(t => t.task_type === stepId);
  };

  // Launch guidance for a step
  const launchGuidance = (step) => {
    setCurrentGuidance(step);
    setShowGuidanceDialog(true);
  };

  // Start documentation for a step
  const startDocumentation = (step) => {
    if (onLaunchTemplate) {
      const task = getTaskForStep(step.id);
      onLaunchTemplate({
        discipline: step.discipline === "all" ? userRole : step.discipline,
        visitType: step.id === "admission_visit" ? "initial" : 
                   step.id === "comprehensive_visit" ? "initial" :
                   step.id.includes("assessment") ? "initial" : "routine",
        taskId: task?.id,
        patientId: workflow?.patient_id,
        patientName: workflow?.patient_name,
        bodySystemsFocus: step.bodySystemsFocus
      });
    }
    setShowGuidanceDialog(false);
  };

  const nextAction = getNextAction();
  const mySteps = getMySteps();
  const progress = calculateProgress();

  // Compact view - just shows next action
  if (compact) {
    return (
      <div className="p-4 bg-gradient-to-r from-teal-900/30 to-emerald-900/30 rounded-lg border border-teal-500/30">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-teal-500/20 rounded-lg">
              <Lightbulb className="h-5 w-5 text-teal-400" />
            </div>
            <div>
              <p className="text-sm text-teal-300 font-medium">Next Step</p>
              {nextAction ? (
                <p className="text-white font-semibold">{nextAction.title}</p>
              ) : (
                <p className="text-green-400 font-semibold">All Steps Complete! ✓</p>
              )}
            </div>
          </div>
          {nextAction && (
            <Button 
              size="sm" 
              className="bg-teal-600 hover:bg-teal-700"
              onClick={() => launchGuidance(nextAction)}
            >
              <Play className="h-4 w-4 mr-1" />
              Start
            </Button>
          )}
        </div>
        {nextAction && (
          <div className="mt-3">
            <Progress value={progress} className="h-2" />
            <p className="text-xs text-slate-400 mt-1">{progress}% complete</p>
          </div>
        )}
      </div>
    );
  }

  return (
    <Card className="bg-slate-800/50 border-slate-700" data-testid="admission-guidance">
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="text-white flex items-center gap-2">
              <Target className="h-5 w-5 text-teal-400" />
              Admission Workflow Guidance
            </CardTitle>
            <CardDescription className="text-slate-400">
              Step-by-step guidance for Medicare-compliant admission
            </CardDescription>
          </div>
          <div className="text-right">
            <p className="text-2xl font-bold text-teal-400">{progress}%</p>
            <p className="text-xs text-slate-400">Complete</p>
          </div>
        </div>
        <Progress value={progress} className="h-2 mt-3" />
      </CardHeader>
      <CardContent>
        {/* Next Action Highlight */}
        {nextAction && (
          <div className="mb-6 p-4 bg-gradient-to-r from-teal-900/30 to-emerald-900/30 rounded-lg border border-teal-500/30">
            <div className="flex items-center gap-2 mb-2">
              <Sparkles className="h-4 w-4 text-teal-400" />
              <span className="text-sm text-teal-300 font-medium">Recommended Next Step</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg bg-${nextAction.color}-500/20`}>
                  <nextAction.icon className={`h-5 w-5 text-${nextAction.color}-400`} />
                </div>
                <div>
                  <p className="text-white font-semibold">{nextAction.title}</p>
                  <p className="text-sm text-slate-400">{nextAction.description}</p>
                </div>
              </div>
              <Button 
                className="bg-teal-600 hover:bg-teal-700"
                onClick={() => launchGuidance(nextAction)}
              >
                <Play className="h-4 w-4 mr-2" />
                Start Now
              </Button>
            </div>
          </div>
        )}

        {/* Timeline */}
        <div className="space-y-3">
          <h4 className="text-sm font-medium text-slate-400 uppercase tracking-wider">
            Your Tasks ({mySteps.length})
          </h4>
          
          <ScrollArea className="h-[400px] pr-4">
            <div className="relative">
              {/* Timeline line */}
              <div className="absolute left-5 top-0 bottom-0 w-0.5 bg-slate-700" />
              
              {mySteps.map((step, index) => {
                const status = getStepStatus(step.id);
                const task = getTaskForStep(step.id);
                const timeRemaining = task ? formatTimeRemaining(task.deadline) : null;
                const isExpanded = expandedStep === step.id;
                const canStart = step.dependsOn ? 
                  step.dependsOn.every(depId => getStepStatus(depId) === "completed") : 
                  true;
                
                return (
                  <div key={step.id} className="relative pl-12 pb-6">
                    {/* Status indicator */}
                    <div className={`absolute left-3 w-5 h-5 rounded-full flex items-center justify-center ring-4 ring-slate-900 ${
                      status === "completed" ? "bg-green-500" :
                      status === "overdue" ? "bg-red-500" :
                      status === "critical" ? "bg-orange-500" :
                      status === "at_risk" ? "bg-yellow-500" :
                      "bg-slate-600"
                    }`}>
                      {status === "completed" ? (
                        <CheckCircle2 className="h-3 w-3 text-white" />
                      ) : status === "overdue" || status === "critical" ? (
                        <AlertCircle className="h-3 w-3 text-white" />
                      ) : (
                        <Circle className="h-3 w-3 text-white" />
                      )}
                    </div>
                    
                    {/* Step card */}
                    <div 
                      className={`p-4 rounded-lg border transition-all cursor-pointer ${
                        status === "completed" ? "bg-green-900/20 border-green-500/30" :
                        status === "overdue" ? "bg-red-900/20 border-red-500/30" :
                        status === "critical" ? "bg-orange-900/20 border-orange-500/30" :
                        isExpanded ? "bg-slate-700/50 border-teal-500/50" :
                        "bg-slate-800/50 border-slate-700 hover:border-slate-600"
                      }`}
                      onClick={() => setExpandedStep(isExpanded ? null : step.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <step.icon className={`h-5 w-5 ${
                            status === "completed" ? "text-green-400" :
                            status === "overdue" ? "text-red-400" :
                            `text-${step.color}-400`
                          }`} />
                          <div>
                            <div className="flex items-center gap-2">
                              <p className={`font-medium ${status === "completed" ? "text-green-300" : "text-white"}`}>
                                {step.title}
                              </p>
                              <Badge variant="outline" className={DISCIPLINE_CONFIG[step.discipline]?.color + " text-white text-xs"}>
                                {DISCIPLINE_CONFIG[step.discipline]?.shortName}
                              </Badge>
                              {step.conditional && (
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger>
                                      <Info className="h-3 w-3 text-slate-400" />
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      <p>{step.conditionLabel}</p>
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              )}
                            </div>
                            <p className="text-sm text-slate-400">{step.description}</p>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          {timeRemaining && status !== "completed" && (
                            <Badge className={timeRemaining.urgent ? "bg-red-500/20 text-red-400" : "bg-slate-700 text-slate-300"}>
                              <Clock className="h-3 w-3 mr-1" />
                              {timeRemaining.text}
                            </Badge>
                          )}
                          {status === "completed" && (
                            <Badge className="bg-green-500/20 text-green-400">Complete</Badge>
                          )}
                          <ChevronRight className={`h-4 w-4 text-slate-400 transition-transform ${isExpanded ? "rotate-90" : ""}`} />
                        </div>
                      </div>
                      
                      {/* Expanded content */}
                      {isExpanded && (
                        <div className="mt-4 pt-4 border-t border-slate-700">
                          <div className="grid grid-cols-2 gap-4">
                            <div>
                              <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Requirements</p>
                              <ul className="space-y-1">
                                {step.requirements.map((req, idx) => (
                                  <li key={idx} className="text-sm text-slate-300 flex items-start gap-2">
                                    <CheckCircle2 className="h-3 w-3 text-slate-500 mt-1 flex-shrink-0" />
                                    {req}
                                  </li>
                                ))}
                              </ul>
                            </div>
                            <div>
                              <p className="text-xs text-slate-400 uppercase tracking-wider mb-2">Timeline</p>
                              <p className="text-sm text-slate-300">
                                <Calendar className="h-3 w-3 inline mr-1" />
                                Due within <strong>{step.hours} hours</strong> of admission
                              </p>
                              {step.dependsOn && (
                                <p className="text-sm text-yellow-400 mt-2">
                                  <AlertTriangle className="h-3 w-3 inline mr-1" />
                                  Requires: {step.dependsOn.map(d => 
                                    ADMISSION_TIMELINE.find(s => s.id === d)?.title
                                  ).join(", ")}
                                </p>
                              )}
                            </div>
                          </div>
                          
                          {status !== "completed" && (
                            <div className="mt-4 flex gap-2">
                              <Button 
                                className="bg-teal-600 hover:bg-teal-700"
                                disabled={!canStart}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  launchGuidance(step);
                                }}
                              >
                                <Play className="h-4 w-4 mr-2" />
                                Start Documentation
                              </Button>
                              {!canStart && (
                                <p className="text-sm text-yellow-400 flex items-center">
                                  <AlertTriangle className="h-4 w-4 mr-1" />
                                  Complete prerequisites first
                                </p>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </ScrollArea>
        </div>
      </CardContent>

      {/* Guidance Dialog */}
      <Dialog open={showGuidanceDialog} onOpenChange={setShowGuidanceDialog}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {currentGuidance && <currentGuidance.icon className={`h-5 w-5 text-${currentGuidance?.color}-400`} />}
              {currentGuidance?.title}
            </DialogTitle>
            <DialogDescription>
              {currentGuidance?.description}
            </DialogDescription>
          </DialogHeader>
          
          {currentGuidance && (
            <div className="space-y-4">
              {/* Requirements checklist */}
              <div className="p-4 bg-slate-900/50 rounded-lg">
                <h4 className="text-sm font-medium text-slate-300 mb-3">
                  What you need to document:
                </h4>
                <ul className="space-y-2">
                  {currentGuidance.requirements.map((req, idx) => (
                    <li key={idx} className="flex items-center gap-2 text-sm text-slate-400">
                      <div className="w-5 h-5 rounded border border-slate-600 flex items-center justify-center">
                        <span className="text-xs text-slate-500">{idx + 1}</span>
                      </div>
                      {req}
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Body systems focus */}
              {currentGuidance.bodySystemsFocus && currentGuidance.bodySystemsFocus.length > 0 && (
                <div className="p-4 bg-teal-900/20 border border-teal-500/30 rounded-lg">
                  <h4 className="text-sm font-medium text-teal-300 mb-2 flex items-center gap-2">
                    <Lightbulb className="h-4 w-4" />
                    Focus on these body systems:
                  </h4>
                  <div className="flex flex-wrap gap-2">
                    {currentGuidance.bodySystemsFocus.map(system => (
                      <Badge key={system} variant="outline" className="border-teal-500/50 text-teal-300">
                        {system.replace(/_/g, " ").replace(/\b\w/g, c => c.toUpperCase())}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Speech-to-text hint */}
              <div className="p-3 bg-violet-900/20 border border-violet-500/30 rounded-lg flex items-center gap-3">
                <Mic className="h-5 w-5 text-violet-400" />
                <div>
                  <p className="text-sm text-violet-300 font-medium">Speech-to-Text Available</p>
                  <p className="text-xs text-violet-400">Use voice dictation to speed up documentation</p>
                </div>
              </div>
              
              {/* Action buttons */}
              <div className="flex justify-end gap-3 pt-4">
                <Button variant="outline" onClick={() => setShowGuidanceDialog(false)}>
                  Cancel
                </Button>
                <Button 
                  className="bg-teal-600 hover:bg-teal-700"
                  onClick={() => startDocumentation(currentGuidance)}
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Open Documentation Form
                  <ArrowRight className="h-4 w-4 ml-2" />
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </Card>
  );
}

// Export timeline for use elsewhere
export { ADMISSION_TIMELINE, DISCIPLINE_CONFIG };
